import React, { useEffect, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-draw";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import ThinFooter from "../Footer/ThinFooter";
import ContentWrapper from "../Atomics/ContentWrapper/ContentWrapper";
import axiosInstance from "../../axiosInstance";
import { useParams, useHistory } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Spinner from "react-bootstrap/Spinner";
import "./edit-config.css";

const EditConfigTrack = () => {
  const { id } = useParams(); // Get configuration ID from route parameters
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tracks, setTracks] = useState([]); // List of tracks
  const [selectedTrack, setSelectedTrack] = useState(null); // Selected track for the configuration
  const [configData, setConfigData] = useState({
    trackName: "",
    configurationName: "",
    direction: "",
  });
  const [map, setMap] = useState(null);
  const [drawnItems, setDrawnItems] = useState(new L.FeatureGroup());

  useEffect(() => {
    fetchConfigurationDetails();
    fetchTracks();
  }, [id]);

  const fetchConfigurationDetails = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axiosInstance.get(`/config/${id}`, {
        headers: { Authorization: token },
      });

      if (response.status === 200 && response.data.success) {
        const { trackName, configurationName, direction } = response.data.data;
        setConfigData({ trackName, configurationName, direction });
      } else {
        toast.error(response.data.error?.message || "Configuration not found.");
        setError(response.data.error?.message || "Configuration not found.");
      }
    } catch (err) {
      console.error("Error fetching configuration:", err);
      setError("Failed to fetch configuration. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchTracks = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axiosInstance.get("/track", {
        headers: { Authorization: token },
      });

      if (response.status === 200 && response.data.success) {
        setTracks(response.data.data);
      } else {
        toast.error("Failed to fetch tracks.");
      }
    } catch (err) {
      console.error("Error fetching tracks:", err);
      toast.error("Error fetching tracks.");
    }
  };

  const initializeMap = () => {
    if (!map) {
      const mapInstance = L.map("config-map", {
        center: [37.7749, -122.4194], // Default to San Francisco
        zoom: 13,
      });

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }).addTo(mapInstance);

      const drawControl = new L.Control.Draw({
        edit: {
          featureGroup: drawnItems,
        },
        draw: {
          polyline: true,
          polygon: true,
          rectangle: true,
          circle: true,
          marker: true,
          circlemarker: false,
        },
      });

      mapInstance.addControl(drawControl);
      drawnItems.addTo(mapInstance);

      mapInstance.on(L.Draw.Event.CREATED, (event) => {
        const { layer } = event;
        drawnItems.addLayer(layer);
        console.log("Shape drawn:", layer.toGeoJSON());
      });

      setMap(mapInstance);
    }
  };

  useEffect(() => {
    if (!loading) {
      initializeMap();
    }
  }, [loading]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setConfigData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSaveChanges = async () => {
    const token = localStorage.getItem("token");

    if (!configData.trackName || !configData.configurationName || !configData.direction) {
      toast.error("Please fill out all required fields.");
      return;
    }

    const selectedTrack = tracks.find((track) => track.trackName === configData.trackName);

    if (!selectedTrack) {
      toast.error("Invalid track selected. Please try again.");
      return;
    }

    const payload = {
      trackId: selectedTrack.trackId,
      configurationName: configData.configurationName,
      direction: configData.direction,
    };

    try {
      const response = await axiosInstance.put(`/config/${id}`, payload, {
        headers: { Authorization: token },
      });

      if (response.status === 200 && response.data.success) {
        toast.success("Configuration updated successfully!");
        history.push("/configurations");
      } else {
        toast.error(response.data.error?.message || "Failed to update configuration.");
      }
    } catch (err) {
      console.error("Error updating configuration:", err);
      toast.error("Failed to update configuration.");
    }
  };

  if (loading) {
    return (
      <Container>
        <Row
          className="text-center w-100"
          style={{ height: "100vh", justifyContent: "center", alignItems: "center" }}
        >
          <Spinner animation="border" />
        </Row>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Row
          className="text-center w-100"
          style={{ height: "100vh", justifyContent: "center", alignItems: "center" }}
        >
          <h4>{error}</h4>
        </Row>
      </Container>
    );
  }

  return (
    <ContentWrapper>
      <Container fluid>
        <Breadcrumb>
          <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
          <Breadcrumb.Item href="/configurations">Configurations</Breadcrumb.Item>
          <Breadcrumb.Item active>Edit Configuration</Breadcrumb.Item>
        </Breadcrumb>

        <Row id="edt_cnfg">
          <Col md={6}>
            <div className="form-group">
              <label>Track Name</label>
              <select
                className="form-control"
                name="trackName"
                value={configData.trackName}
                onChange={handleInputChange}
              >
                <option value="">Select Track</option>
                {tracks.map((track) => (
                  <option key={track.trackId} value={track.trackName}>
                    {track.trackName}
                  </option>
                ))}
              </select>
            </div>
          </Col>
          <Col md={6}>
            <div className="form-group">
              <label>Direction</label>
              <select
                className="form-control"
                name="direction"
                value={configData.direction}
                onChange={handleInputChange}
              >
                <option value="">Select Direction</option>
                <option value="Clockwise">Clockwise</option>
                <option value="Counterclockwise">Counterclockwise</option>
              </select>
            </div>
          </Col>
        </Row>

        <Row id="edt_cnfg1">
          <Col md={12}>
            <div className="form-group">
              <label>Configuration Name</label>
              <input
                type="text"
                className="form-control"
                name="configurationName"
                value={configData.configurationName}
                onChange={handleInputChange}
                placeholder="Enter Configuration Name"
              />
            </div>
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            <div id="config-map" style={{ display: "none", height: "400px", marginTop: "20px" }}></div>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col md={12} className="text-right">
            <button className="btn btn-primary sv_btn" onClick={handleSaveChanges}>
              Update
            </button>
          </Col>
        </Row>
      </Container>
      <ThinFooter />
      <Toaster />
    </ContentWrapper>
  );
};

export default EditConfigTrack;
